import {
  ArchiveIcon,
  BuildingsIcon,
  ChartBarIcon,
  ChartLineIcon,
  ClockCounterClockwiseIcon,
  CubeIcon,
  DesktopTowerIcon,
  GearSixIcon,
  HeadsetIcon,
  LifebuoyIcon,
  MonitorIcon,
  TagIcon,
  UserListIcon,
  UsersThreeIcon,
  type Icon,
} from "@phosphor-icons/react";

import type { PlatformRole } from "../auth/roles";

/**
 * The route table for the management workspace.
 *
 * Each entry names the audience it is drawn for, taken from the personas in
 * `docs/PRD.md` section 4 and the capabilities in section 6.6. Navigation and
 * the route guard read the same entry, so a link can never appear for a screen
 * the shell would then refuse to draw, and the audience cannot be changed in
 * one place only.
 *
 * `allowedRoles` is a drawing decision. It is not a permission. See
 * `src/auth/RouteAccessGate.tsx`.
 *
 * Every screen behind these paths is unbuilt. Each one states what it will show
 * and what it is waiting for; none of them invents a figure to stand in.
 *
 * ## This whole table is public
 *
 * The build is one static bundle served by Cloudflare Pages, so every string
 * below is readable by anyone who can fetch the page, whatever role they claim
 * and whether or not they are signed in. Code-splitting would only move the
 * strings into another URL anyone can request, so it would buy secrecy for
 * nothing.
 *
 * The rule that follows is therefore a content rule, enforced by
 * `route-manifest.test.ts`: this table describes the *product*, never a shop.
 * No tenant, customer, staff member, figure or identifier belongs in it. Screen
 * content arrives from the API, behind the server-side authorization that
 * ticket P1-014 owns, and is filtered there rather than here.
 */

/**
 * Sidebar groups, by job (Musweu, 29 September 2026; ticket P3-011 slice 7).
 * A role sees only its own screens, so a group with none is not drawn.
 */
export type WorkspaceRouteGroup = "today" | "stock" | "customers" | "owner" | "platform" | "support";

export type WorkspaceRoute = {
  /** Absolute path, matching the `to` a link would use. */
  path: string;
  /** Short label for navigation. */
  navLabel: string;
  /** The shortest label, for the phone's bottom bar. */
  shortLabel: string;
  /** The screen's own heading, and the document title. */
  title: string;
  icon: Icon;
  group: WorkspaceRouteGroup;
  allowedRoles: readonly PlatformRole[];
  /** What this screen is for, in the shop's language. */
  purpose: string;
  /** What it will show once it has a source. Never a figure, never a sample. */
  willShow: readonly string[];
  /** Why it has nothing to show today. */
  awaiting: string;
  /**
   * The ADR 0023 permission the screen's API will require, where it is
   * settled. Documentation only: the server enforces it, and scopes the data
   * to the caller's shop (P1-014). A route drawn for managers must never name
   * an Admin-only permission; see `route-manifest.test.ts`.
   */
  permission?: string;
  /**
   * Drawn only when the session's shop lists this permission too: a screen
   * a Manager sees once an Admin has given them the right (P5-013 W6). Like
   * `allowedRoles`, a drawing decision; the server decides.
   */
  drawnWith?: string;
};

/**
 * ADR 0023 permissions that only an Admin holds. A route drawn for managers
 * must not depend on one of these; Admin-only content gets its own route.
 */
export const ADMIN_ONLY_PERMISSIONS: readonly string[] = [
  "catalogue.manage",
  "catalogue.price_release.approve",
  "staff.manage",
  "shop.settings",
  "terminal.manage",
  "support_grant.manage",
  "data.export",
  "staff.cashier.manage",
  "terminal.activate",
  "terminal.revoke",
];

const SHOP_OPERATIONS: readonly PlatformRole[] = ["manager", "admin"];
const SHOP_ADMINISTRATION: readonly PlatformRole[] = ["admin"];
const PLATFORM: readonly PlatformRole[] = ["superadmin"];
const SUPPORT: readonly PlatformRole[] = ["support"];

export const WORKSPACE_ROUTES: readonly WorkspaceRoute[] = [
  {
    allowedRoles: SHOP_OPERATIONS,
    // Built (P5-004; for Managers too since P5-013 W4, ADR 0023 amendment):
    // OverviewScreen. These texts describe the delivered screen.
    awaiting: "Nothing: this screen reads the shop's figures from the server directly.",
    group: "today",
    icon: ChartLineIcon,
    navLabel: "Overview",
    path: "/overview",
    shortLabel: "Overview",
    permission: "report.dashboard",
    purpose: "How the shop is trading, from what its tills have sent.",
    title: "Shop overview",
    willShow: [
      "Sales for today or a chosen period",
      "Gross profit where cost is known",
      "Open shifts, and each till's last sync",
    ],
  },
  {
    allowedRoles: SHOP_OPERATIONS,
    // Built (P5-013 W4): ReportsScreen. These texts describe the delivered
    // screen. Downloading needs data.export, which only Admins hold.
    awaiting: "Nothing: this screen reads the shop's figures from the server directly.",
    group: "owner",
    icon: ChartBarIcon,
    navLabel: "Period reports",
    path: "/reports",
    shortLabel: "Reports",
    permission: "report.dashboard",
    purpose: "How the shop traded over a chosen period, day by day.",
    title: "Period reports",
    willShow: [
      "Sales for each day of a chosen period",
      "Gross profit, only where the cost price is known",
      "Stock value at cost",
    ],
  },
  {
    allowedRoles: SHOP_OPERATIONS,
    // Built (P5-013 W1): TillHealthScreen. These texts describe the
    // delivered screen.
    awaiting: "Nothing: this screen reads the shop's tills from the server directly.",
    group: "today",
    icon: MonitorIcon,
    navLabel: "Till health",
    path: "/tills",
    permission: "terminal.view",
    shortLabel: "Tills",
    purpose:
      "Whether the shop's till is reporting, so a quiet connection reads as stale rather than as no sales.",
    title: "Till health",
    willShow: [
      "Each till by name, and whether it is in touch",
      "When each till last checked in, and apart from that its last accepted sync",
      "A till not heard from for longer than the shop's offline threshold",
    ],
  },
  {
    allowedRoles: SHOP_OPERATIONS,
    // Built (P5-004 slice D-4): ShiftsScreen and ZReportScreen. These texts
    // describe the delivered screen; the planned screen is no longer drawn.
    awaiting: "Shifts appear here once the shop's till has synced them.",
    group: "today",
    icon: ClockCounterClockwiseIcon,
    navLabel: "Shifts and cash",
    path: "/shifts",
    shortLabel: "Shifts",
    purpose: "Each shift the tills have synced, and a Z report for each closed one.",
    title: "Shifts and cash",
    willShow: [
      "Each shift with its till, cashier, state and the till's last sync, without cash figures",
      "A closed shift's Z report: sales, drawer events, expected and counted cash and the variance",
      "The Z report as a CSV download, or printed and saved as PDF",
    ],
  },
  {
    allowedRoles: SHOP_OPERATIONS,
    // Built (P5-013 W3): StockScreen. These texts describe the delivered
    // screen.
    awaiting: "Stock appears here once the shop's till has synced it.",
    group: "stock",
    icon: CubeIcon,
    navLabel: "Stock",
    path: "/stock",
    permission: "report.shop",
    shortLabel: "Stock",
    purpose: "What the shop holds, what it is worth, and what is running out, as its tills have synced it.",
    title: "Stock",
    willShow: [
      "Stock on hand by product, and its value at cost for those who may see cost",
      "Products at or below their reorder level",
      "Each delivery, sale, count, write-off and adjustment, with its till",
    ],
  },
  {
    allowedRoles: SHOP_OPERATIONS,
    // Built (P5-013 W2): CatalogueScreen, read only. These texts describe
    // the delivered screen.
    awaiting: "The catalogue appears here once an Admin sets it up on the till.",
    group: "stock",
    icon: TagIcon,
    navLabel: "Catalogue",
    path: "/catalogue",
    permission: "catalogue.view",
    shortLabel: "Catalogue",
    purpose: "The shop's products, prices and barcodes, from the latest catalogue release the server accepted.",
    title: "Catalogue",
    willShow: [
      "Each active product by name, with its category and tax",
      "Its units or price points, with their sizes, prices and barcodes",
      "The release shown, when BurgePOS accepted it, and the till it came from",
    ],
  },
  {
    allowedRoles: SHOP_ADMINISTRATION,
    awaiting: "There is no identity service, so there are no staff accounts to administer.",
    group: "owner",
    icon: UsersThreeIcon,
    navLabel: "Staff and access",
    path: "/staff",
    shortLabel: "Staff",
    purpose: "Who works in the shop, what each person may approve, and how access is withdrawn.",
    title: "Staff and access",
    willShow: [
      "Staff accounts with their role and their till PIN state",
      "Which approvals each role may give",
      "Revocations, and when the till received them",
    ],
    permission: "staff.manage",
  },
  {
    allowedRoles: ["manager"],
    // Built (P5-013 W6): CashiersScreen, for a Manager an Admin has given
    // Staff; the Admin does the same on Staff and access.
    awaiting: "Nothing: this screen reads the shop's Cashiers from the server directly.",
    drawnWith: "staff.cashier.manage",
    group: "owner",
    icon: UserListIcon,
    navLabel: "Shop cashiers",
    path: "/cashiers",
    permission: "staff.cashier.manage",
    shortLabel: "Cashiers",
    purpose: "Adding Cashiers and setting up their till PINs, which the shop's Admin has allowed you to do.",
    title: "Shop cashiers",
    willShow: ["The shop's Cashiers and whether each has a till PIN", "Adding a Cashier", "A one-time till PIN setup code"],
  },
  {
    allowedRoles: SHOP_ADMINISTRATION,
    awaiting: "Nothing: this screen reads the shop's tills from the server directly.",
    group: "owner",
    icon: DesktopTowerIcon,
    navLabel: "Tills and devices",
    path: "/devices",
    permission: "terminal.manage",
    shortLabel: "Tills",
    purpose: "The shop's till, and anything waiting for the Admin before a till is enrolled, kept running or replaced.",
    title: "Tills and devices",
    willShow: [
      "The till with its key protection and when it enrolled",
      "Activations to approve, and key exceptions or successions to acknowledge",
      "Revoking a lost or replaced till",
    ],
  },
  {
    allowedRoles: SHOP_ADMINISTRATION,
    awaiting: "Nothing: this screen reads and gives support access through the server directly.",
    group: "owner",
    icon: HeadsetIcon,
    navLabel: "Support access",
    path: "/support-access",
    permission: "support_grant.manage",
    shortLabel: "Support",
    purpose: "Letting a named BurgePOS support operator read chosen parts of the shop for a short, set time.",
    title: "Support access",
    willShow: [
      "Giving a support operator read access, with a reason and a ticket reference",
      "Access given, whether it is active, scheduled or ended, and until when",
      "Ending access early",
    ],
  },
  {
    allowedRoles: SHOP_OPERATIONS,
    // Built (P5-013 W5a): ShopSettingsScreen for Managers and Admins, and
    // BusinessSettingsScreen at /settings/business for shop.settings. These
    // texts describe the delivered screen.
    awaiting: "Nothing: this screen reads and saves the shop's settings on the server directly.",
    group: "owner",
    icon: GearSixIcon,
    navLabel: "Shop settings",
    path: "/settings",
    permission: "receipt.template.manage",
    shortLabel: "Settings",
    purpose: "What every till prints on its receipts, and for the Admin, the business record, the offline alert and the business's colours and logo.",
    title: "Shop settings",
    willShow: [
      "The receipt details every till prints, who last saved them, and which version each till uses",
      "Saving new receipt details, which each till uses from the next shift it opens while online",
      "For the Admin, the business details and how long a till may be quiet before Till health warns",
      "For the Admin, the business's colours and logo, which everyone signed in to the business sees",
    ],
  },
  {
    allowedRoles: PLATFORM,
    awaiting: "Tenant provisioning endpoints are not implemented.",
    group: "platform",
    icon: BuildingsIcon,
    navLabel: "Tenants",
    path: "/platform/tenants",
    shortLabel: "Tenants",
    purpose: "Provisioning and activation for every shop on the platform.",
    title: "Tenants",
    willShow: [
      "Tenants with their activation and billing state",
      "Pending onboarding requests awaiting a verified setup payment",
      "Support access grants, each time-limited and approved by the shop owner",
    ],
  },
  {
    allowedRoles: PLATFORM,
    awaiting: "Retention and erasure controls are not implemented.",
    group: "platform",
    icon: ArchiveIcon,
    navLabel: "Retention and erasure",
    path: "/platform/governance",
    shortLabel: "Retention",
    purpose: "Retention windows, erasure requests and the record of what was purged.",
    title: "Retention and erasure",
    willShow: [
      "Retention policy per data class",
      "Erasure requests and their eligibility",
      "Completed purges as append-only records",
    ],
  },
  {
    allowedRoles: SUPPORT,
    awaiting: "Nothing: this screen reads the access shops have given you from the server directly.",
    group: "support",
    icon: LifebuoyIcon,
    navLabel: "Support work",
    path: "/support",
    shortLabel: "Support",
    purpose: "The access shop owners have given you, and starting to use it.",
    title: "Support work",
    willShow: [
      "Access given to you, with its reason, ticket reference and end time",
      "Starting to use access after confirming it is you",
      "A shop's tills, when the access includes them",
    ],
  },
];

export const WORKSPACE_ROUTES_BY_PATH = new Map(
  WORKSPACE_ROUTES.map((route) => [route.path, route] as const),
);

/**
 * The first screen a role can actually open. Used for the workspace entry
 * point, so nobody is redirected straight into a route their claim cannot see.
 * Returns `null` when the role has no workspace at all.
 */
export function landingPathFor(role: PlatformRole | null): string | null {
  if (role === null) {
    return null;
  }
  return WORKSPACE_ROUTES.find((route) => route.allowedRoles.includes(role))?.path ?? null;
}

export function routesVisibleTo(role: PlatformRole | null, permissions: readonly string[] = []): readonly WorkspaceRoute[] {
  if (role === null) {
    return [];
  }
  return WORKSPACE_ROUTES.filter(
    (route) => route.allowedRoles.includes(role) && (route.drawnWith === undefined || permissions.includes(route.drawnWith)),
  );
}

export const GROUP_ORDER: readonly WorkspaceRouteGroup[] = [
  "today",
  "stock",
  "customers",
  "owner",
  "platform",
  "support",
];

export const GROUP_LABELS: Record<WorkspaceRouteGroup, string> = {
  customers: "Customers",
  owner: "Owner",
  platform: "Platform",
  stock: "Stock",
  support: "Support",
  today: "Today",
};

/**
 * Each role's most-used screens, for the phone's bottom bar, in bar order
 * (Musweu, 29 September 2026). Everything else is under More.
 */
export const PHONE_PRIMARY_PATHS: Record<PlatformRole, readonly string[]> = {
  admin: ["/overview", "/stock", "/catalogue", "/reports"],
  cashier: [],
  manager: ["/overview", "/shifts", "/stock", "/tills"],
  superadmin: ["/platform/tenants", "/platform/governance"],
  support: ["/support"],
};

/** The bottom bar's screens for a role, never one the role cannot open. */
export function phonePrimaryRoutes(role: PlatformRole | null): readonly WorkspaceRoute[] {
  if (role === null) {
    return [];
  }
  return PHONE_PRIMARY_PATHS[role]
    .map((path) => WORKSPACE_ROUTES_BY_PATH.get(path))
    .filter((route): route is WorkspaceRoute => route?.allowedRoles.includes(role) ?? false);
}

/**
 * The Shop settings tabs below /settings, which the manifest does not list
 * as screens of their own. Each is drawn by shop.settings: an Admin's by role
 * or a Manager's by grant (P5-013 W6), and the shell names it "Shop settings"
 * only for those, so a refused tab still discloses nothing.
 */
export const SETTINGS_TABS: ReadonlyMap<
  string,
  { title: string; allowedRoles: readonly PlatformRole[]; drawnWith: string }
> = new Map([
  ["/settings/business", { allowedRoles: ["manager", "admin"], drawnWith: "shop.settings", title: "Shop settings" }],
  ["/settings/branding", { allowedRoles: ["manager", "admin"], drawnWith: "shop.settings", title: "Shop settings" }],
]);

/**
 * Detail screens below a manifest route. They take the access of `parent`, so
 * the router and the shell's title both read this one list: a detail screen
 * the router draws can never fall through to "Page not found".
 */
export const Z_REPORT_ROUTE = { parent: "/shifts", path: "/shifts/:shiftId", title: "Z report" };
export const DETAIL_ROUTES: readonly { parent: string; path: string; title: string }[] = [Z_REPORT_ROUTE];
